import { useCallback } from "react"
import type { WhatsappMessage } from "@stockline/shared/types/communications/whatsapp-message"
import { WHATSAPP_MESSAGE_TABLE } from "../../../lib/offline/resources/communications/whatsapp-message/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

/**
 * Build an optimistic WhatsappMessage payload. The caller fills in
 * `chatUid`, `content`, `messageType`. The returned row has a temp `id`,
 * a current `timestamp` (in seconds, matches the canonical schema), and
 * `messageMetadata.__optimistic = true` for downstream filtering.
 */
function buildOptimisticMessage(opts: {
  tempId: string
  companyId: string
  userId: string
  chatUid: string
  content: Record<string, unknown>
  messageType: number
}): WhatsappMessage {
  const nowIso = new Date().toISOString()
  return {
    id: opts.tempId,
    companyId: opts.companyId,
    userId: opts.userId,
    chatUid: opts.chatUid,
    chatJid: null,
    whatsappKeyId: null,
    fromJid: null,
    fromName: null,
    fromNumber: null,
    content: opts.content,
    messageType: opts.messageType,
    isFromMe: true,
    mediaUrl: null,
    aiAnalysis: null,
    mediaExtraction: null,
    messageMetadata: { __optimistic: true },
    status: 2,
    timestamp: Math.floor(Date.now() / 1000),
    syncId: 0,
    createdAt: nowIso,
    updatedAt: nowIso,
    deletedAt: null,
  }
}

export interface InjectOptimisticInput {
  companyId: string
  userId: string
  chatUid: string
  content: Record<string, unknown>
  messageType: number
}

/**
 * Optimistic write helpers for WhatsApp messages.
 *
 * Writes go directly into Dexie's `whatsappMessages` table — the source of
 * truth for the chat UI (via `useWhatsappMessagesPaged`). The legacy REST
 * send still kicks off the real delivery; this hook is purely about the
 * instant client-side appearance. Reconciliation happens when the real
 * message arrives via V2 SSE delta poll: callers should call
 * `removeOptimistic(tempId)` either on send failure or once they detect the
 * real counterpart (matching `whatsappKeyId`) in Dexie.
 *
 * Why not push through the V2 change queue: there is no POST handler for
 * `whatsapp-messages` on the V2 API yet. Sends still go through the legacy
 * `/api/whatsapp/chats/{id}/send-message` REST endpoint, which routes to
 * Evolution, then loops back into Dexie via SSE.
 */
export function useWhatsappMessageMutations() {
  const { storage } = useAISync()

  const injectOptimistic = useCallback(
    async (input: InjectOptimisticInput): Promise<string> => {
      const tempId = `optimistic-${crypto.randomUUID()}`
      const row = buildOptimisticMessage({ tempId, ...input })
      await storage.put(WHATSAPP_MESSAGE_TABLE, row)
      return tempId
    },
    [storage]
  )

  const removeOptimistic = useCallback(
    async (tempId: string): Promise<void> => {
      await storage.delete(WHATSAPP_MESSAGE_TABLE, tempId)
    },
    [storage]
  )

  /**
   * Wait for a real (non-optimistic) message with the given `whatsappKeyId`
   * to appear in Dexie, then delete the optimistic row. Polls every 500ms,
   * bails after `timeoutMs`. The bail-out path still deletes the temp so the
   * UI never accumulates ghosts — worst case the user sees a 1–2s gap before
   * the real message lands.
   *
   * Lookup goes through the `whatsappKeyId` index (see
   * `WHATSAPP_MESSAGE_TABLE_SCHEMA`), so each tick is an O(log n) point
   * query rather than a full-table scan.
   */
  const reconcileWhenRealArrives = useCallback(
    (tempId: string, whatsappKeyId: string, timeoutMs = 30_000) => {
      const POLL_MS = 500
      const start = Date.now()
      const tick = async () => {
        try {
          const candidates = await storage.getAll<WhatsappMessage>(
            WHATSAPP_MESSAGE_TABLE,
            { whatsappKeyId }
          )
          const real = candidates.find(
            (m) =>
              m.id !== tempId &&
              !(m.messageMetadata as { __optimistic?: boolean } | null)
                ?.__optimistic
          )
          if (real) {
            await storage.delete(WHATSAPP_MESSAGE_TABLE, tempId)
            return
          }
        } catch {
          // Swallow read errors; try again on next tick or bail on timeout.
        }
        if (Date.now() - start > timeoutMs) {
          // Real message never showed up (rare). Delete the temp so we don't
          // leave a permanent ghost in Dexie.
          await storage.delete(WHATSAPP_MESSAGE_TABLE, tempId).catch(() => {})
          return
        }
        setTimeout(tick, POLL_MS)
      }
      setTimeout(tick, POLL_MS)
    },
    [storage]
  )

  return { injectOptimistic, removeOptimistic, reconcileWhenRealArrives }
}
